import {
  View,
  RadioGroup,
  RadioGroupItem,
  Image,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui";
import PayNowImage from "@/assets/images/paynow.png";
import MasterCardImage from "@/assets/images/master-card.png";
import AmericanExpressImage from "@/assets/images/american-express.png";
import { Bank } from "@/components/shared/icons";
import { ApplyCredit } from "../apply-credit";
import type { PaymentType } from "@/store/invoices";
import { Typography } from "@/components/shared/typography";
import { Divider } from "@/components/shared/divider";
import { formatPrice, SINGAPORE_TAX } from "@/lib/helpers/number";
import { IfElse } from "@/components/shared/if-else";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import { AvailableVoucher } from "../../invoice-detail/available-voucher";
import { InfoCard } from "@/components/shared/info-card";
import type { SelectPaymentScreenModalProps } from "../type";

export const SelectPaymentScreenModal = ({
  open,
  onCloseSelectPayment,
  onPay,
  onPayButtonText,
  onApplyAllMain,
  onApplyAllMassage,
  control,
  selectPayment,
  setSelectPayment,
  loading,
  baseAmount,
  amount,
  paidAmount,
  balance,
  appliedVoucher,
  appliedMainCredit,
  appliedMassageCredit,
  applyMainCredit,
  applyMassageCredit,
  isSelectCreditCard,
  isSelectPayNow,
  disableApplyAllMain,
  disableApplyAllMassage,
  departmentCreditType,
  departmentCreditLabel,
  isTopUpPayment,
}: SelectPaymentScreenModalProps) => {
  const isAllPaymentsDisabled = disableApplyAllMain && disableApplyAllMassage;

  return (
    <Dialog open={open} onOpenChange={onCloseSelectPayment}>
      <DialogContent className="h-full gap-0 overflow-scroll bg-white p-0 md:max-h-[720px]">
        <DialogTitle>
          <View className="flex flex-row items-center p-4">
            <IconButton
              iconName="arrowLeft"
              color="foreground"
              onClick={onCloseSelectPayment}
              variant="tertiary"
            />
            <Typography>Select Payment Method</Typography>
          </View>
        </DialogTitle>
        <View className="flex flex-col gap-4">
          {isTopUpPayment && (
            <View className="border-y border-secondary-border p-4">
              <InfoCard description="Your top-up invoice is valid for 24 hours. After this period, it will be automatically annulled." />
            </View>
          )}
          <View className="px-4">
            <ApplyCredit
              control={control}
              onApplyAllMain={onApplyAllMain}
              onApplyAllMassage={onApplyAllMassage}
              disableApplyAllMain={disableApplyAllMain}
              disableApplyAllMassage={disableApplyAllMassage}
              departmentCreditType={departmentCreditType}
            />
          </View>
          <Divider />
          <View className="px-4">
            <RadioGroup
              value={selectPayment}
              onValueChange={(val) => {
                setSelectPayment(val as PaymentType);
              }}
              disabled={isAllPaymentsDisabled}
            >
              <Card
                {...(isAllPaymentsDisabled
                  ? {}
                  : { onClick: () => setSelectPayment("credit-card") })}
              >
                <CardContent
                  className={cn(
                    isSelectCreditCard &&
                      "rounded-xl border border-primary-border",
                  )}
                >
                  <View className="flex flex-col gap-1">
                    <View className="flex flex-row justify-between">
                      <View className="flex flex-row gap-[10px]">
                        <Image src={MasterCardImage} width={46} height={32} />
                        <Image
                          src={AmericanExpressImage}
                          width={53}
                          height={32}
                        />
                      </View>
                      <RadioGroupItem value="credit-card" checkCircle />
                    </View>

                    <Typography variant="body-sm" color="foreground-default">
                      Credit / Debit Card
                    </Typography>

                    {isSelectCreditCard && (
                      <View className="flex flex-col gap-2">
                        <Typography
                          variant="body-sm"
                          color="foreground-intermediate"
                        >
                          We impose a surcharge of 3.4% + $0.5 for Debit &
                          Credit Cards only, which goes towards card processing
                          costs and is not greater than our cost of acceptance.
                          Unfortunately, we currently don't accept Visa, but you
                          can proceed with other payment networks. Alternative,
                          pay with PayNow with no additional fees.
                        </Typography>
                        <Typography
                          variant="body-sm"
                          color="foreground-intermediate"
                        >
                          Payment status will be updated within 7 days due to
                          bank reconciliation process.
                        </Typography>
                      </View>
                    )}
                  </View>
                </CardContent>
              </Card>
              <Card
                {...(isAllPaymentsDisabled
                  ? {}
                  : { onClick: () => setSelectPayment("paynow") })}
              >
                <CardContent
                  className={cn(
                    isSelectPayNow && "rounded-xl border border-primary-border",
                  )}
                >
                  <View className="flex flex-col gap-1">
                    <View className="flex flex-row justify-between">
                      <View className="flex flex-row items-center gap-[10px]">
                        <Image src={PayNowImage} width={52} height={32} />
                        <Bank
                          weight="fill"
                          className="text-primary"
                          size={32}
                        />
                      </View>
                      <RadioGroupItem value="paynow" checkCircle />
                    </View>

                    <Typography variant="body-sm" color="foreground-default">
                      PayNow / Bank Transfer
                    </Typography>

                    {isSelectPayNow && (
                      <View className="flex flex-col gap-2">
                        <Typography
                          variant="body-sm"
                          color="foreground-intermediate"
                        >
                          You will be redirected to new page to complete the
                          payment via Paynow.
                        </Typography>
                        <Typography
                          variant="body-sm"
                          color="foreground-intermediate"
                        >
                          Payment status will be updated within 7 days due to
                          bank reconciliation process.
                        </Typography>
                      </View>
                    )}
                  </View>
                </CardContent>
              </Card>
            </RadioGroup>
          </View>
          <Divider />
          <View className="flex flex-col gap-2 px-4">
            <Typography variant="label-sm">Vouchers</Typography>
            <AvailableVoucher rightVariant="badge" />
          </View>
          <Divider />
          <View className="flex flex-col gap-2 px-4">
            <Typography variant="body-md">Payment Details</Typography>
            <Card>
              <CardContent>
                <View className="flex w-full flex-row justify-between">
                  <Typography variant="body-sm" color="foreground-intermediate">
                    Subtotal
                  </Typography>
                  <Typography variant="body-sm">{baseAmount}</Typography>
                </View>
                {appliedVoucher > 0 && (
                  <View className="flex w-full flex-row justify-between">
                    <Typography
                      variant="body-sm"
                      color="foreground-intermediate"
                    >
                      Discount Voucher
                    </Typography>
                    <Typography variant="body-sm">
                      -{formatPrice(appliedVoucher)}
                    </Typography>
                  </View>
                )}
                {!!amount && (
                  <View className="flex w-full flex-row justify-between">
                    <Typography
                      variant="body-sm"
                      color="foreground-intermediate"
                    >
                      {`GST (${SINGAPORE_TAX * 100}%)`}
                    </Typography>
                    <Typography variant="body-sm" color="foreground-default">
                      {formatPrice(amount)}
                    </Typography>
                  </View>
                )}
                {applyMainCredit && (
                  <View className="flex w-full flex-row justify-between">
                    <Typography
                      variant="body-sm"
                      color="foreground-intermediate"
                    >
                      Main Credit Account
                    </Typography>
                    <Typography variant="body-sm" color="foreground-default">
                      -{appliedMainCredit}
                    </Typography>
                  </View>
                )}
                {applyMassageCredit && (
                  <View className="flex w-full flex-row justify-between">
                    <Typography
                      variant="body-sm"
                      color="foreground-intermediate"
                    >
                      {`${departmentCreditLabel} Credit Account`}
                    </Typography>
                    <Typography variant="body-sm" color="foreground-default">
                      -{appliedMassageCredit}
                    </Typography>
                  </View>
                )}
                <IfElse if={!!paidAmount}>
                  <View className="flex w-full flex-row justify-between">
                    <Typography
                      variant="body-sm"
                      color="foreground-intermediate"
                    >
                      Payment Confirmed
                    </Typography>
                    <Typography variant="body-sm" color="foreground-default">
                      -{formatPrice(paidAmount)}
                    </Typography>
                  </View>
                </IfElse>
                <Divider className="my-2" />
                <View className="flex w-full flex-row justify-between">
                  <Typography variant="body-sm" color="foreground-default">
                    Total amount
                  </Typography>
                  <View className="flex flex-row items-end gap-1">
                    <Typography variant="body-sm" color="brand-primary">
                      {balance}
                    </Typography>
                    {!!amount && (
                      <Typography
                        variant="body-sm"
                        color="foreground-intermediate"
                      >
                        incl. GST
                      </Typography>
                    )}
                  </View>
                </View>
              </CardContent>
            </Card>
          </View>
          <View className="sticky bottom-0 flex flex-col gap-4 bg-white p-4">
            <View className="flex w-full flex-row justify-between px-1">
              <Typography variant="body-sm" color="foreground-default">
                Balance Due
              </Typography>
              <View className="flex flex-row items-end gap-1">
                <Typography variant="body-sm" color="brand-primary">
                  {balance}
                </Typography>
                {!!amount && (
                  <Typography
                    variant="body-sm"
                    color="foreground-intermediate"
                    className="mb-[2px]"
                  >
                    incl. GST
                  </Typography>
                )}
              </View>
            </View>
            <View className="flex w-full flex-row gap-2">
              <Button
                variant="secondary"
                color="CTA2"
                fullWidth="full"
                onClick={onCloseSelectPayment}
                children="Cancel"
                disabled={loading}
              />
              <Button
                variant="primary"
                color="CTA"
                fullWidth="full"
                onClick={onPay}
                children={onPayButtonText}
                disabled={loading}
              />
            </View>
          </View>
        </View>
      </DialogContent>
    </Dialog>
  );
};
